<template>
  <v-layout column>
    <vbt-search
      v-model="searchData"
      :items="searchFieldsConfig"
      @reset="onResetSearch"
      @search="onSearch"
    />

    <vbt-content-box
      :loading="isLoading"
      :loading-error="loadingError"
      :no-content="noContent"
      :go-back-btn="goBack"
      title="Information"
    >
      <template #toolbar>
        <v-layout
          align-center
          justify-end
        >
          <v-flex lg3>
            <v-layout align-center>
              <strong class="body-1 mr-2">
                Show only active
              </strong>
              <v-switch
                v-model="activeOnly"
                color="success"
                class="vbt-hide-form-control-details"
              />
            </v-layout>
          </v-flex>

          <ums-sync-azure-users @refresh="$_refreshData" />

          <v-btn
            small
            class="ml-4"
            color="success"
            @click="openEditorDialog()"
          >
            Create user
          </v-btn>
        </v-layout>
      </template>
      <vbt-table
        :headers="headers"
        :items="users"
        :disable-edit-btn="checkEditActionDisable"
        show-edit-action
        show-details-action
        @click:edit="openEditorDialog"
        @click:details="({ id }) => $router.push({
          name: 'user',
          params: { id },
          query: { pageNum: '1' },
        })"
      >
        <template #prepend-actions="{ item }">
          <v-layout>
            <ums-user-state-switcher
              :id="item.id"
              :name="item.name"
              :type="item.type"
              :state="item.active"
              @success="$_fetchData"
            />
            <ums-password-change-activator
              :disabled="!item.active"
              :updatable-name="item.name"
              @click="openPasswordChangerDialog(item)"
            />
          </v-layout>
        </template>
      </vbt-table>

      <vbt-pagination
        v-model="pageNum"
        :items-count="pagedItemsCount"
      />
    </vbt-content-box>

    <ums-user-editor
      v-model="editorDialog"
      :creator="isEditorCreateMode"
      :item="editableUser"
      @success="$_fetchData"
    />

    <ums-user-password-changer
      v-model="passwordChangerDialog"
      :user="editableUser"
      @success="$_fetchData"
    />
  </v-layout>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import {
  useEditorWithDialog,
  useDataLoading,
  usePagination,
  useSearch,
} from '@mixins/factories';
import { handlerActiveOnly } from '@mixins';

import { namespace as EnumsNamespace } from '@store/modules/enums.types';
import {
  namespace as TenantsNamespace,
  ActionTypes as TenantsActionTypes,
} from '@modules/tenants/store.types';

import { namespace as UsersNamespace, ActionTypes as UsersActionTypes } from '../store.types';

import UmsUserEditor from '../_components/UmsUserEditor.vue';
import UmsUserStateSwitcher from '../_components/UmsUserStateSwitcher.vue';
import UmsUserPasswordChanger from '../_components/UmsUserPasswordChanger.vue';
import UmsSyncAzureUsers from '../_components/UmsSyncAzureUsers.vue';

const dataLoadingOptions = {
  getterName: UsersActionTypes.GET_USERS,
  specialQueryParams: [{ type: 'booleanString', name: 'activeOnly' }],
  contentName: 'users',
};
const paginationOptions = { contentName: 'users' };
const searchOptions = {
  systemQueryParams: ['pageNum', 'activeOnly'],
  initialSearchDataStates: [
    'name', 'email', 'type',
  ],
};

const headers = Object.freeze([
  { text: 'Name', value: 'name', sortable: true },
  { text: 'Email', value: 'email', sortable: true },
  { text: 'Tenant', value: 'tenant' },
  { text: 'OID', value: 'id' },
  { text: 'State', value: 'state' },
  { text: 'Type', value: 'type' },
]);

export default {
  name: 'UmsUsers',
  components: {
    UmsUserEditor,
    UmsUserStateSwitcher,
    UmsUserPasswordChanger,
    UmsSyncAzureUsers,
  },
  mixins: [
    useEditorWithDialog({ editableItemName: 'editableUser' }),
    useEditorWithDialog({
      editorDialogFlagName: 'passwordChangerDialog',
      openTriggerName: 'openPasswordChangerDialog',
      editableItemName: 'editableUser',
    }),
    useDataLoading(dataLoadingOptions),
    usePagination(paginationOptions),
    useSearch(searchOptions),
    handlerActiveOnly,
  ],
  data: () => ({ headers }),
  computed: {
    ...mapState(UsersNamespace, [
      'users',
      'usersNamesAutocomplete',
      'usersEmailsAutocomplete',
    ]),
    ...mapState(EnumsNamespace, {
      userTypeOptions: ({ enums: { userType } }) => (userType || [])
        .map((v) => ({ value: v, text: v })),
    }),
    ...mapState(TenantsNamespace, {
      tenantsOptions: ({ allTenants }) => allTenants
        .map(({ name: v }) => ({ value: v, text: v })),
    }),
    searchFieldsConfig() {
      return [
        {
          flex: 'lg3',
          value: 'name',
          label: 'Name',
          type: 'autocomplete',
          placeholder: 'Start typing...',
          items: this.usersNamesAutocomplete,
          callback: this.getUsersNamesAutocomplete,
        },
        {
          flex: 'lg3',
          value: 'email',
          label: 'Email',
          type: 'autocomplete',
          placeholder: 'Start typing...',
          items: this.usersEmailsAutocomplete,
          callback: this.getUsersEmailsAutocomplete,
        },
        {
          flex: 'lg3',
          value: 'tenant',
          label: 'Tenant',
          type: 'select',
          items: this.tenantsOptions,
        },
        {
          flex: 'lg3',
          value: 'type',
          label: 'Type',
          type: 'select',
          items: this.userTypeOptions,
        },
      ];
    },
  },
  created() {
    this.getTenants();
  },
  methods: {
    ...mapActions(UsersNamespace, [UsersActionTypes.GET_USERS]),
    ...mapActions(UsersNamespace, {
      getUsersNamesAutocomplete: UsersActionTypes.GET_USERS_NAMES_AUTOCOMPLETE,
      getUsersEmailsAutocomplete: UsersActionTypes.GET_USERS_EMAILS_AUTOCOMPLETE,
    }),
    ...mapActions(TenantsNamespace, {
      getTenants: TenantsActionTypes.GET_ALL_TENANTS,
    }),
    checkEditActionDisable: ({ active }) => !active,
  },
};
</script>
